import { useCallback, useState } from 'react';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  SheetScaleType,
  SheetShapeDeepFragment,
  SheetsDeepFragment,
} from '../gql/graphql';
import { Point2 } from '../domain/geometry/geometric-types';
import {
  findCurrentCalibration,
  getUnitScaleFromCalibration,
} from '../domain/sheet-calibration';

/**
 * Clipboard state for copy/paste operations across sheets
 */
export type ShapeClipboardState = {
  /** Deep cloned shapes ready for pasting */
  shapes: SheetShapeDeepFragment[];
  /** Source sheet ID where shapes were copied from */
  sourceSheetId: string;
  /** Source page number where shapes were copied from */
  sourcePageNumber: number;
  /** Source calibration for scaling calculations */
  sourceCalibration: SheetScaleType;
  /** Average center point of all shape vertices */
  center: Point2;
};

/**
 * Calculate the average center point of all vertices in the given shapes
 */
export function getAverageCenter(shapes: SheetShapeDeepFragment[]): Point2 {
  const allPoints: Point2[] = [];

  shapes.forEach((shape) => {
    if (shape.sheetShapePolygon) {
      shape.sheetShapePolygon.multipolygon.polygons.forEach((poly) => {
        poly.exterior.points.forEach((p) => allPoints.push([p.x, p.y]));
        poly.interiors.forEach((interior) => {
          interior.points.forEach((p) => allPoints.push([p.x, p.y]));
        });
      });
    } else if (shape.sheetShapeLine) {
      shape.sheetShapeLine.points.forEach((p) => allPoints.push([p.x, p.y]));
    } else if (shape.sheetShapePoint) {
      allPoints.push([
        shape.sheetShapePoint.point.x,
        shape.sheetShapePoint.point.y,
      ]);
    } else if (shape.sheetShapeLegend) {
      allPoints.push([
        shape.sheetShapeLegend.position.x,
        shape.sheetShapeLegend.position.y,
      ]);
    } else if (shape.sheetShapeSplitLine) {
      shape.sheetShapeSplitLine.points.forEach((p) =>
        allPoints.push([p.x, p.y])
      );
    }
  });

  if (allPoints.length === 0) {
    return [0, 0];
  }

  const avgX = allPoints.reduce((sum, p) => sum + p[0], 0) / allPoints.length;
  const avgY = allPoints.reduce((sum, p) => sum + p[1], 0) / allPoints.length;
  return [avgX, avgY];
}

/**
 * Calculate the scale factor to preserve physical dimensions when pasting
 * from source to target calibration
 */
export function calculateScaleFactor(
  sourceCalibration: SheetScaleType,
  targetCalibration: SheetScaleType
): number {
  const sourceScale = getUnitScaleFromCalibration(sourceCalibration);
  const targetScale = getUnitScaleFromCalibration(targetCalibration);
  // scaleFactor converts source PDF units to target PDF units preserving real-world size
  return sourceScale / targetScale;
}

type UseCopyPasteShapesProps = {
  /** All sheets with their calibrations */
  sheets: Pick<SheetsDeepFragment, 'sheetPageCalibrations' | 'id'>[] | null;
  /** Currently active sheet ID */
  activeSheetId: string | null;
  /** Current page number */
  pageNumber: number;
};

/**
 * Hook for managing copy/paste of sheet shapes across pages
 */
export function useCopyPasteShapes({
  sheets,
  activeSheetId,
  pageNumber,
}: UseCopyPasteShapesProps) {
  const { t } = useTranslation('project');
  const toast = useToast();

  const [clipboardState, setClipboardState] =
    useState<ShapeClipboardState | null>(null);
  const [isPasting, setIsPasting] = useState(false);

  /**
   * Get the current page's calibration
   */
  const getCurrentCalibration = useCallback(() => {
    return findCurrentCalibration(sheets, activeSheetId, pageNumber);
  }, [sheets, activeSheetId, pageNumber]);

  /**
   * Copy selected shapes to clipboard
   * Returns true if copy was successful, false otherwise
   */
  const copyShapes = useCallback(
    (shapes: SheetShapeDeepFragment[]): boolean => {
      if (shapes.length === 0) {
        return false;
      }

      // Validate that all shapes are from the same sheet/page
      const firstShape = shapes[0];
      const sourceSheetId = firstShape.sheetId;
      const sourcePageNumber = firstShape.sheetPageNumber;

      // Check if source page has calibration
      const calibration = findCurrentCalibration(
        sheets,
        sourceSheetId,
        sourcePageNumber
      );

      if (!calibration) {
        toast({
          title: t('shapes.copy-paste.source-not-calibrated-title'),
          description: t('shapes.copy-paste.source-not-calibrated-description'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return false;
      }

      // Deep clone shapes to avoid mutation issues
      const clonedShapes: SheetShapeDeepFragment[] = JSON.parse(
        JSON.stringify(shapes)
      );

      // Calculate center point
      const center = getAverageCenter(clonedShapes);

      setClipboardState({
        shapes: clonedShapes,
        sourceSheetId,
        sourcePageNumber,
        sourceCalibration: calibration.calibration,
        center,
      });

      toast({
        title: t('shapes.copy-paste.copied-title', { count: shapes.length }),
        status: 'success',
        duration: 2000,
        isClosable: true,
      });

      return true;
    },
    [sheets, toast, t]
  );

  /**
   * Start paste mode - validates target calibration first
   * Returns true if paste mode was started, false otherwise
   */
  const startPasteMode = useCallback((): boolean => {
    if (!clipboardState) {
      return false;
    }

    // Check if target page has calibration
    const targetCalibration = getCurrentCalibration();

    if (!targetCalibration) {
      toast({
        title: t('shapes.copy-paste.target-not-calibrated-title'),
        description: t('shapes.copy-paste.target-not-calibrated-description'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return false;
    }

    setIsPasting(true);
    return true;
  }, [clipboardState, getCurrentCalibration, toast, t]);

  /**
   * Cancel paste mode
   */
  const cancelPaste = useCallback(() => {
    setIsPasting(false);
  }, []);

  /**
   * Clear the clipboard
   */
  const clearClipboard = useCallback(() => {
    setClipboardState(null);
    setIsPasting(false);
  }, []);

  /**
   * Get the scale factor for pasting to the current page
   */
  const getScaleFactorForCurrentPage = useCallback((): number => {
    if (!clipboardState) {
      return 1;
    }

    const targetCalibration = getCurrentCalibration();
    if (!targetCalibration) {
      return 1;
    }

    return calculateScaleFactor(
      clipboardState.sourceCalibration,
      targetCalibration.calibration
    );
  }, [clipboardState, getCurrentCalibration]);

  /**
   * Check if pasting to the same page as the source
   */
  const isSamePagePaste = useCallback((): boolean => {
    if (!clipboardState || !activeSheetId) {
      return false;
    }
    return (
      clipboardState.sourceSheetId === activeSheetId &&
      clipboardState.sourcePageNumber === pageNumber
    );
  }, [clipboardState, activeSheetId, pageNumber]);

  return {
    clipboardState,
    isPasting,
    hasClipboard: clipboardState !== null,
    copyShapes,
    startPasteMode,
    cancelPaste,
    clearClipboard,
    getScaleFactorForCurrentPage,
    isSamePagePaste,
    setIsPasting,
  };
}
